// src/components/ceremony/useCeremonyAudio.js
// ============================================================================
// The ceremony's music bed (founder: "STARTING SOON b-roll starts, music
// playing" / "awaiting results b-roll with music").
//
// SOUND ON BY DEFAULT, WITH AN HONEST FALLBACK (founder 2026-08-07: "Sound auto
// off, should be auto on").
//
// The autoplay wall is real and we still do not fight it: Chrome and Safari
// refuse AUDIBLE autoplay until the page has a user gesture, so "always on"
// cannot be promised. What we can do is TRY. We attempt unmuted playback first;
// if the browser refuses, we fall back to a muted bed plus the SOUND ON
// affordance instead of leaving the beat in silence.
//
// In practice the common path succeeds: a spectator who clicked through from
// the lobby or Live Matches has already given the document a gesture, and
// Chrome's media engagement heuristics allow it from there.
//
// A refusal is NOT remembered — only an explicit tap of the control writes the
// session preference, so a browser block never turns into a sticky opt-out.
//
// It also NEVER plays over a live feed. Music belongs to the b-roll phases; the
// moment the cameras take the screen the bed stops, so it can't fight the race
// audio of a spectator who unmuted the stream.
//
// The app ships no audio library and no bundled sound (the only noise it makes
// today is a WebAudio beep in MatchReadyContext), so this is a plain <audio>
// element against a static file — the same way the landing hero video is
// served out of web-landing/web/media/.
// ============================================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { Platform } from 'react-native';

import { CEREMONY_MUSIC_URLS, CEREMONY_PHASE } from '../../constants/ceremony';

const IS_WEB = Platform.OS === 'web';
const PREF_KEY = 'cc_ceremony_sound';

// Phases that carry a music bed. LIVE and OFF deliberately do not.
const MUSICAL_PHASES = new Set([
  CEREMONY_PHASE.COUNTDOWN,
  CEREMONY_PHASE.STARTING_SOON,
  CEREMONY_PHASE.GRID_LOCK,
  CEREMONY_PHASE.RESULTS,
]);

const FADE_MS = 700;
const TARGET_VOLUME = 0.55;

export default function useCeremonyAudio(phase, { enabled = true } = {}) {
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  // Position in the rotation. A ref, not state: advancing a track must not
  // re-run the play/stop effect, which would restart the fade mid-song.
  const trackRef = useRef(0);
  const [soundOn, setSoundOn] = useState(() => {
    if (!IS_WEB || typeof window === 'undefined') return false;
    try {
      // Only an explicit tap of SOUND OFF opts out for the session. Anything
      // else (no preference yet, or storage unavailable) starts ON and lets the
      // browser be the one to say no.
      return window.sessionStorage?.getItem(PREF_KEY) !== '0';
    } catch (err) {
      return true;
    }
  });

  const wanted = enabled && IS_WEB && MUSICAL_PHASES.has(phase);

  // --- element lifecycle ---------------------------------------------------
  useEffect(() => {
    if (!IS_WEB || typeof document === 'undefined') return undefined;
    const el = document.createElement('audio');
    // NO src and preload='none' here. This hook is called by CeremonyStage on
    // every EventWatch mount, before it knows whether a ceremony will ever run
    // — setting src eagerly made every spectator on every live event download
    // 3.8MB of music they were never going to hear. The source is attached the
    // first time a musical phase actually arrives.
    // Only loop the element when there is nothing to rotate to; with a real
    // playlist the 'ended' handler below is what keeps the bed going.
    el.loop = CEREMONY_MUSIC_URLS.length <= 1;
    el.preload = 'none';
    el.muted = true;      // set on the element, not via a React prop — React
    el.volume = 0;        // does not reliably reflect `muted` onto the DOM
    el.setAttribute('aria-hidden', 'true');
    audioRef.current = el;

    // Advance the rotation. muted/volume live on the element and survive a src
    // swap, so the bed keeps whatever the listener chose across track changes.
    const onEnded = () => {
      if (CEREMONY_MUSIC_URLS.length <= 1) return;
      trackRef.current = (trackRef.current + 1) % CEREMONY_MUSIC_URLS.length;
      el.src = CEREMONY_MUSIC_URLS[trackRef.current];
      el.play?.().catch(() => {});
    };
    el.addEventListener('ended', onEnded);

    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
      el.removeEventListener('ended', onEnded);
      try { el.pause(); } catch (err) { /* already torn down */ }
      // removeAttribute + load(), not src='': assigning an empty string makes
      // the browser resolve it against the page URL and refetch the document.
      el.removeAttribute('src');
      try { el.load(); } catch (err) { /* fine */ }
      audioRef.current = null;
    };
  }, []);

  // --- play / stop by phase ------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;

    if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }

    if (wanted) {
      // Attach the source on first real need (see the mount effect).
      if (!el.getAttribute('src')) {
        el.src = CEREMONY_MUSIC_URLS[trackRef.current];
        el.preload = 'auto';
      }
      el.muted = !soundOn;
      // Try for sound. If the browser refuses audible autoplay (no gesture
      // banked yet), don't leave the beat silent AND don't leave a dead
      // element: drop to a muted bed and raise the SOUND ON affordance so one
      // tap fixes it. Deliberately does NOT write the session preference — a
      // browser refusal is not the user choosing silence.
      const started = el.play?.();
      if (started && typeof started.catch === 'function') {
        started.catch(() => {
          if (!soundOn) return; // already muted; nothing left to fall back to
          el.muted = true;
          el.play?.().catch(() => {});
          setSoundOn(false);
        });
      }
      const target = soundOn ? TARGET_VOLUME : 0;
      const steps = Math.max(1, Math.round(FADE_MS / 50));
      let i = 0;
      const from = el.volume;
      fadeRef.current = setInterval(() => {
        i += 1;
        el.volume = Math.max(0, Math.min(1, from + ((target - from) * i) / steps));
        if (i >= steps && fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }
      }, 50);
    } else {
      const from = el.volume;
      const steps = Math.max(1, Math.round(FADE_MS / 50));
      let i = 0;
      fadeRef.current = setInterval(() => {
        i += 1;
        el.volume = Math.max(0, from * (1 - i / steps));
        if (i >= steps) {
          if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }
          try { el.pause(); el.currentTime = 0; } catch (err) { /* fine */ }
        }
      }, 50);
    }

    return () => {
      if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }
    };
  }, [wanted, soundOn]);

  // --- pause when the tab is hidden ---------------------------------------
  useEffect(() => {
    if (!IS_WEB || typeof document === 'undefined') return undefined;
    const onVis = () => {
      const el = audioRef.current;
      if (!el) return;
      if (document.visibilityState === 'hidden') el.pause?.();
      else if (wanted) el.play?.().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [wanted]);

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      try { window.sessionStorage?.setItem(PREF_KEY, next ? '1' : '0'); } catch (err) { /* fine */ }
      const el = audioRef.current;
      if (el) {
        el.muted = !next;
        // The toggle IS the gesture — this play() is the one that's allowed to
        // make noise, so it must happen inside the handler, not in an effect.
        if (next) el.play?.().catch(() => {});
      }
      return next;
    });
  }, []);

  return {
    soundOn,
    toggleSound,
    // Only worth offering the control when there's actually a bed to unmute.
    canOfferSound: wanted,
  };
}
